import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import env from '@app/environment'

import Screen from '@app/components/Screen/Screen'
import { EntryFlowTipsterInfo, InstallIOSPopup } from '@app/components'
import {
  Button,
  Divider,
  Icon,
  IconColor,
  IconSize,
  IconTypes,
  Modal,
} from '@app/ui'
import {
  // ONBOARDING DISABLED FOR TIPSTERIZER - BrowserMapInitialKey and BrowsersMap were in onboarding.ts
  // BrowserMapInitialKey,
  // BrowsersMap,
  // LOGOTYPE_IMAGE_SRC,
  PosthogEvent,
  ROUTES,
} from '@app/constants'
import { ButtonSize, ButtonType, ModalType } from '@app/types'
import { useBrandConfig } from '@app/hooks/useBrandConfig'
import { InstallAppContext } from '@app/context'
import { CurrentUserState } from '@app/storage'
import { UserRoles } from '@app/graphql'
import { renderToStaticMarkup } from 'react-dom/server'
import {  getToken } from '@app/helpers/auth'
import { useSnapshot } from 'valtio'
import { CalendarHelper } from '@app/helpers'
import { initDB, Stores } from '@app/helpers/db'
import { captureAnalytics } from '@app/helpers/misc'


interface EntryScreenProps { }

interface DomainTitleProps {
  weight?: number;
  size?: number;
  lineHeight?: number;
  paddingTop?: number;
  fontStyle?: string
}

export const DomainTitleComponent: React.FC<DomainTitleProps> = ({ weight, size, lineHeight, paddingTop, fontStyle }) => {
  const { t } = useTranslation()
  return (<DomainTitle
    weight={weight}
    size={size}
    paddingTop={paddingTop}
    lineHeight={lineHeight}
    fontStyle={fontStyle}>
    <Divider size="small" />
    {t('login.domaiN')}
  </DomainTitle>
  )
}

const EntryScreen: React.FC<EntryScreenProps> = () => {
  const { id: userId } = useSnapshot(CurrentUserState)
  const { t } = useTranslation()
  const { brandName } = useBrandConfig()
  // ONBOARDING DISABLED FOR TIPSTERIZER - BrowserMapInitialKey was in onboarding.ts
  const [ currentBrowser, setCurrentBrowser ] = useState('')
  const [ openInstallIOSModal, setOpenInstallIOSModal ] = useState(false)
  const [ openInstallModal, setOpenInstallModal ] = useState(false)
  const [ showWelcomeModal, setShowWelcomeModal ] = useState(true)
  const { search: queryParams } = useLocation()
  const { event: installEvent } = useContext(InstallAppContext)
  const navigate = useNavigate()

  const onCloseInstallIOSModal = () => setOpenInstallIOSModal(false)
  const onCloseInstallModal = () => setOpenInstallModal(false)
  const installApp = async () => {
    try {
      await installEvent.prompt()
      await installEvent.userChoice
      captureAnalytics(PosthogEvent.APP_INSTALLED, {})
    } finally {
      setOpenInstallModal(false)
    }
  }
  const onChangeBrowser = (id) => () => {
    if (id) setCurrentBrowser(id)
  }
  const onStart = (isGuest = false) => {
    if(isGuest){
      navigate(ROUTES.feed.short + (queryParams || ''))
    } else {
      // ONBOARDING DISABLED FOR TIPSTERIZER
      // localStorage.setItem('onboardingFlow', UserRoles.User)
      navigate(ROUTES.login.short + (queryParams || ''))
    }
  }

  useEffect(() => {
    const worker = new Worker(new URL('@app/workers/feed/matchList.ts', import.meta.url))
    async function matchListPostMessage() {
      const authType = (localStorage.getItem('auth_type') || '').trim().toLowerCase()
      const incognitoMode = sessionStorage.getItem('incognitoMode') ?? ''
      const token = await getToken()
      const tokenType = authType === 'telegram' ? 'Telegram' : authType === 'magic_link' ? 'MagicLink' : 'Bearer'
      for(const skip of [ 0, 1, 2, ]) {
        worker.postMessage({
          headers: {
            authorization:  `${tokenType} ${token}`,
            'X-Incognito-Mode': incognitoMode,
          },
          variables: {
            skip: skip * 10,
            limit: 10,
            eventsDate: CalendarHelper.getDayAsString(new Date()),
            forDefaultBookie: true,
            sportId: '1',
          },
          url: env.API_URL,
        })
      }
    }
    const channelListworker = new Worker(new URL('@app/workers/channel/list.ts', import.meta.url))
    async function channelListPostMessage() {
      const authType = (localStorage.getItem('auth_type') || '').trim().toLowerCase()
      const incognitoMode = sessionStorage.getItem('incognitoMode') ?? ''
      const token = await getToken()
      const tokenType = authType === 'telegram' ? 'Telegram' : authType === 'magic_link' ? 'MagicLink' : 'Bearer'
      channelListworker.postMessage({
        headers: {
          authorization:  `${tokenType} ${token}`,
          'X-Incognito-Mode': incognitoMode,
        },
        variables: {
          skip: 0,
          limit: 50,
          filter: { sportsId: null, categories: null }
        },
        url: env.API_URL,
      })
    }
    async function runWorkers() {
      await initDB([
        { storeName: Stores.MATCHES, keyPath: 'cursor' },
        { storeName: Stores.CHANNEL_LIST, keyPath: 'cursor' }
      ])
      matchListPostMessage()
      channelListPostMessage()
    }
    runWorkers()
    return () => {
      worker.terminate()
      channelListworker.terminate()
    }
  }, [])
  // ONBOARDING DISABLED FOR TIPSTERIZER - BrowsersMap was in onboarding.ts
  const browserVideo = { prevId: null, nextId: null, name: '', videoUrl: '' }

  return (
    <Container>
      <BackgroundImage>
        <EntryFlowTipsterInfo />
        <ContinueButtonWrapper data-login-continue-anchor>
          <WelcomeButton
            type={ButtonType.primary}
            fluid
            onClick={() => onStart(false)}
          >
            {t('common.continue')}
          </WelcomeButton>
        </ContinueButtonWrapper>
      </BackgroundImage>


      {/* MODALS */}
      {openInstallModal && (
        <StyledModal open onClose={onCloseInstallModal}>
          <div className="install-content vb-flex-column-fs-c">
            <StyledModalHeader className="vb-flex-row-spb-c">
              <button
                className="left-arrow vb-flex-row-fs-c"
                onClick={onChangeBrowser(browserVideo.prevId)}
              >
                {browserVideo?.prevId ? (
                  <Icon
                    type={IconTypes.arrowLeft}
                    color={IconColor.brandDark}
                    size={IconSize.large}
                  />
                ) : null}
              </button>
              <p className="vb-font big w700 brandDarker">
                {browserVideo.name}
              </p>
              <button
                className="right-arrow vb-flex-row-fs-c "
                onClick={onChangeBrowser(browserVideo.nextId)}
              >
                {browserVideo?.nextId ? (
                  <Icon
                    type={IconTypes.arrowLeft}
                    color={IconColor.brandDark}
                    size={IconSize.large}
                  />
                ) : null}
              </button>
            </StyledModalHeader>
            <div className="install-demo">
              <video controls muted playsInline>
                <source src={browserVideo.videoUrl} type="video/mp4" />
              </video>
            </div>
            <div className="buttons-block vb-flex-row-spb-c">
              <Button
                size={ButtonSize.normal}
                className="vb-font w700"
                onClick={onCloseInstallModal}
              >
                {t('login.notNow')}
              </Button>
              <Button
                className="vb-font w700"
                size={ButtonSize.normal}
                type={ButtonType.primary}
                onClick={installApp}
              >
                {t('login.installApp')}
              </Button>
            </div>
          </div>
        </StyledModal>
      )}
      <InstallIOSPopup
        open={openInstallIOSModal}
        onClose={onCloseInstallIOSModal}
      />
    </Container>
  )
}

const DomainTitle = styled.div<DomainTitleProps>`
  font-weight:  ${(props) => (props.weight ? props.weight : 900)};
  font-style:   ${(props) => (props.fontStyle ? props.fontStyle : 'italic')};
  font-size:    ${(props) => (props.size ? props.size : 30.41)}px;
  font-family:  Nunito-Bold;
  line-height:  ${(props) => (props.lineHeight ? props.lineHeight : 34.65)}px;
  padding: 20px 0px;

    @media only screen and (max-width: 375px) and (max-height: 667px) {
      display: none;
    }

`

const StyledModal = styled(Modal)`
  height: 100%;

  .modal-content {
    padding: 0 16px;
    height: 80%;
  }
  .install-content {
    height: 100%;
  }
  .install-demo {
    max-height: calc(100% - 120px);
    padding: 16px 0;
    flex: 1;

    video {
      height: 100%;
      object-fit: contain;
    }
  }
  .buttons-block {
    width: 100%;
  }
`

const StyledModalHeader = styled.div`
  height: 60px;
  width: 100%;

  .left-arrow,
  .right-arrow {
    min-width: 90px;
    background-color: transparent;
    border: none;
    padding: 0 24px;
  }
  .right-arrow {
    transform: rotateY(180deg);
    transform-origin: center center;
  }
`

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  height: 100vh;
  margin: 0 auto;
  overflow: hidden;
  background: #141414;
  background-image: url('/assets/images/welcome-background.svg?v=3');
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;

  [data-theme="light"] & {
    background: var(--bg-primary);
  }
`

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const ContinueButtonWrapper = styled.div`
  position: absolute;
  bottom: 24px;
  left: 16px;
  right: 16px;
`

const CenteredLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`

const LogoText = styled.h2`
  font-size: 16px;
  font-weight: 400;
  color: var(--text-primary);
`

const WelcomeModal = styled(Modal)`
  .type-bottom-sheet {
    padding: 0px !important;
    z-index: 1000;
    background: var(--bg-secondary);
    border-radius: 20px 20px 0 0;
  }
`

const ModalContent = styled.div`
  position: relative;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const WelcomeTitle = styled.h1`
  font-size: var(--font-size-extra-big);
  color: var(--text-primary);
  text-align: center;
  margin-bottom: 32px;
  font-weight: 500;
`

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`

const WelcomeButton = styled(Button)`
  padding: 16px 24px !important;
  border-radius: 8px !important;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s ease;

  &.type-primary {
    background: var(--bg-quinary);
    color: white;
  }

  &.type-transparent {
    background: transparent;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  &:active {
    transform: scale(0.98);
  }
`

const DisclaimerText = styled.p`
  color: var(--text-septenary);
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`

export default EntryScreen
